import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { validateSupportToken } from "lib/api/Support";
import PageLayout from "components/layouts/PageLayout";
import TextInput from "components/common/TextInput";
import TextArea from "components/common/TextArea";
import Button from "components/common/Button";
import ButtonGroup from "components/common/ButtonGroup";
import Validation from "components/common/Validation";
import SelectInput from "components/common/SelectInput";
import FileInput from "components/common/FileInput";

const FIELDS = [
  { key: "name", label: "Contact Name", type: "text", required: true },
  { key: "phone", label: "Phone Number", type: "tel", required: true },
  { key: "email", label: "Email", type: "email", required: true },
  {
    key: "contactMethod",
    label: "Preferred Contact Method",
    type: "select",
    options: ["Email", "Phone"],
    required: true,
  },
];

const EMPTY_FORM = Object.fromEntries([
  ...FIELDS.map((f) => [f.key, ""]),
  ["message", ""],
  ["sendCopy", true],
]);

const REQUIRED = [
  ...FIELDS.filter((f) => f.required).map((f) => f.key),
  "message",
];

export default function TrainingRequest(props) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachments, setAttachments] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const history = useHistory();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const idParam = urlParams.get("id");

  useEffect(() => {
    validateSupportToken(urlParams.get("id"), urlParams.get("token")).then(
      (status) => {
        if (status === 403) {
          history.replace(
            "/support/off-maintenance?id=" +
              idParam +
              "&token=" +
              urlParams.get("token")
          );
        } else if (status !== 200) {
          history.replace("/support/unavailable");
        }
      }
    );
  }, [search, history]);

  const handleChange = (field) => (e) => {
    const value = e && e.target !== undefined ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const localValidate = () => {
    for (const f of REQUIRED) {
      if (!`${form[f]}`.trim()) {
        if (f === "contactMethod")
          return "Please select a preferred contact method.";
        if (f === "message") return "Please enter a description.";
        return `Please enter ${f}.`;
      }
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) return "Please enter a valid email.";
    if (form.phone && !/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(form.phone))
      return "Please enter a valid phone number.";
    return "";
  };

  const submit = async () => {
    const msg = localValidate();
    if (msg) {
      setError(msg);
      setSuccess("");
      return;
    }

    try {
      setSending(true);
      setError("");

      const hasFiles = attachments.length > 0;
      const body = hasFiles
        ? new FormData()
        : JSON.stringify({ ...form, id: idParam });

      if (hasFiles) {
        Object.entries(form).forEach(([k, v]) => body.append(k, v));
        body.append("id", idParam);
        attachments.forEach(
          (file, idx) => body.append(`attachment_${idx}`, file) // one name per file
        );
      }

      const rsp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/support/forms/training-request`,
        {
          method: "POST",
          headers: hasFiles ? {} : { "Content-Type": "application/json" },
          body,
        }
      );
      const json = await rsp.json();
      if (rsp.ok) {
        setSuccess(json.message || "Thank you! Your request was sent.");
        setForm(EMPTY_FORM);
        setAttachments([]);
        setFileInputKey((prev) => prev + 1);
      } else {
        setError(json.message || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout hideNav centerLogo supportMode {...props}>
      <h1>Request Training Services</h1>
      <div className="hr hr--darkest"></div>
      <p>
        Please fill out the form below to request training services.
        <br />
        <br />
        We appreciate your business and look forward to assisting you as quickly
        as possible.
      </p>

      {error && <Validation className="danger">{error}</Validation>}
      {success && <Validation className="success">{success}</Validation>}

      <div className="row">
        {FIELDS.map(({ key, label, type, required, options }) => (
          <div className="col-xs-12 col-sm-6" key={key}>
            {type === "select" ? (
              <SelectInput
                label={label}
                required={required}
                placeholder="Select…"
                className="form-control"
                name={key}
                value={form[key]}
                onChange={handleChange(key)}
                options={options}
              />
            ) : (
              <TextInput
                name={key}
                label={label}
                required={required}
                placeholder={label}
                type={type}
                value={form[key]}
                onChange={handleChange(key)}
              />
            )}
          </div>
        ))}

        <div className="col-xs-12">
          <TextArea
            name="message"
            label="Description"
            required={true}
            placeholder="Please be as detailed as possible..."
            rows="8"
            value={form.message}
            onChange={handleChange("message")}
          />
        </div>

        <div className="col-xs-12">
          <FileInput
            key={fileInputKey}
            label="Attachments"
            multiple
            onChange={(e) => setAttachments(Array.from(e.target.files))}
          />
        </div>
      </div>

      <div className="m-bot-20" />

      <p>
        A copy of your request will be emailed to the address entered above.
      </p>

      <div className="row">
        <div className="col-xs-12 col-sm-4">
          <ButtonGroup>
            <Button disabled={sending} onClick={submit}>
              Submit Request
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </PageLayout>
  );
}
